import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Plus, Search, FileText, Eye, Edit, Trash2, Paperclip, Copy, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export const VoucherListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Determine base path based on current route (for accounts vs employee)
  const isAccountsPath = location.pathname.startsWith('/accounts');
  const basePath = isAccountsPath ? '/accounts/vouchers' : '/vouchers';

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const data = await vouchersApi.getAll();
      setVouchers(data);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await vouchersApi.delete(deleteId);
      setVouchers(vouchers.filter((v) => v.id !== deleteId));
      toast.success('Voucher deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete voucher');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDuplicate = async (voucherId) => {
    setDuplicating(voucherId);
    try {
      const newVoucher = await vouchersApi.duplicate(voucherId);
      setVouchers([newVoucher, ...vouchers]);
      toast.success('Voucher duplicated successfully');
      // Navigate to edit the new voucher with a flag indicating it's from duplication
      navigate(`${basePath}/${newVoucher.id}/edit`, { state: { fromDuplication: true } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate voucher');
    } finally {
      setDuplicating(null);
    }
  };

  const filteredVouchers = vouchers.filter((v) => {
    const matchesSearch =
      v.beneficiaryName?.toLowerCase().includes(search.toLowerCase()) ||
      v.voucherNo?.toLowerCase().includes(search.toLowerCase()) ||
      v.poNumber?.toLowerCase().includes(search.toLowerCase()) ||
      v.department?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Export to CSV function
  const handleExportCSV = () => {
    if (filteredVouchers.length === 0) {
      toast.error('No vouchers to export');
      return;
    }

    const headers = ['Voucher No', 'PO Number', 'Date', 'Beneficiary', 'Department', 'Amount', 'Currency', 'Status'];
    
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    const csvData = filteredVouchers.map(v => [
      escapeCSV(v.voucherNo || 'Draft'),
      escapeCSV(v.poNumber || ''),
      escapeCSV(formatDate(v.date)),
      escapeCSV(v.beneficiaryName || ''),
      escapeCSV(v.department || ''),
      escapeCSV(v.amountTotal || 0),
      escapeCSV(v.currency || 'SAR'),
      escapeCSV(v.status || '')
    ]);

    // Add UTF-8 BOM for Excel compatibility with special characters (Arabic, etc.)
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vouchers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV exported successfully');
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredVouchers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVouchers = filteredVouchers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voucher-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">My Vouchers</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage and track your payment vouchers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={filteredVouchers.length === 0}
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={() => navigate(`${basePath}/new`)}
            className="bg-[#6a5da4] hover:bg-[#5a4d94]"
            data-testid="create-voucher-button"
          >
            <Plus className="w-4 h-4 mr-2" /> New Voucher
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by beneficiary, voucher no, PO number, department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="SUBMITTED">Submitted</SelectItem>
                <SelectItem value="HOLD">On Hold</SelectItem>
                <SelectItem value="MANAGER_APPROVED">Manager Approved</SelectItem>
                <SelectItem value="MANAGER_REJECTED">Manager Rejected</SelectItem>
                <SelectItem value="ADDITIONAL_MANAGER_APPROVED">2nd Manager Approved</SelectItem>
                <SelectItem value="ADDITIONAL_MANAGER_REJECTED">2nd Manager Rejected</SelectItem>
                <SelectItem value="INTERNAL_AUDIT_APPROVED">Audit Approved</SelectItem>
                <SelectItem value="INTERNAL_AUDIT_REJECTED">Audit Rejected</SelectItem>
                <SelectItem value="ACCOUNTS_APPROVED">Accounts Approved</SelectItem>
                <SelectItem value="ACCOUNTS_REJECTED">Accounts Rejected</SelectItem>
                <SelectItem value="FINANCE_HEAD_APPROVED">Finance Approved</SelectItem>
                <SelectItem value="FINANCE_HEAD_REJECTED">Finance Rejected</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No vouchers found</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first voucher to get started'}
              </p>
              {!search && statusFilter === 'all' && (
                <Button
                  onClick={() => navigate(`${basePath}/new`)}
                  className="bg-[#6a5da4] hover:bg-[#5a4d94]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Voucher
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Voucher No</TableHead>
                    <TableHead className="font-semibold">PO Number</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Beneficiary</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedVouchers.map((voucher) => (
                    <TableRow
                      key={voucher.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`${basePath}/${voucher.id}`)}
                      data-testid={`voucher-row-${voucher.id}`}
                    >
                      <TableCell className="font-mono font-medium">
                        <div className="flex items-center gap-1">
                          {voucher.voucherNo || (
                            <span className="text-muted-foreground">Draft</span>
                          )}
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {voucher.poNumber || '-'}
                      </TableCell>
                      <TableCell>{formatDate(voucher.date)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{voucher.beneficiaryName}</p>
                          <p className="text-xs text-muted-foreground">{voucher.department}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={voucher.status} size="sm" />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`${basePath}/${voucher.id}`)}
                            data-testid={`view-voucher-${voucher.id}`}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(voucher.id)}
                            disabled={duplicating === voucher.id}
                            data-testid={`duplicate-voucher-${voucher.id}`}
                            title="Duplicate"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {voucher.status === 'DRAFT' && (voucher.createdBy === user?.id || user?.role === 'admin') && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`${basePath}/${voucher.id}/edit`)}
                                data-testid={`edit-voucher-${voucher.id}`}
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteId(voucher.id)}
                                data-testid={`delete-voucher-${voucher.id}`}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {paginatedVouchers.map((voucher) => (
                  <div
                    key={voucher.id}
                    className="bg-white border rounded-lg p-4 shadow-sm"
                    onClick={() => navigate(`${basePath}/${voucher.id}`)}
                    data-testid={`voucher-card-${voucher.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-mono font-semibold text-[#6a5da4]">
                          {voucher.voucherNo || 'Draft'}
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <Paperclip className="w-3.5 h-3.5 inline ml-1 text-[#6a5da4]" />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(voucher.date)}</p>
                      </div>
                      <StatusBadge status={voucher.status} size="sm" />
                    </div>
                    <div className="mb-3">
                      <p className="font-medium text-sm">{voucher.beneficiaryName}</p>
                      <p className="text-xs text-muted-foreground">{voucher.department}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-mono font-semibold">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </p>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/${voucher.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicate(voucher.id)}
                          disabled={duplicating === voucher.id}
                          className="text-blue-600"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {voucher.status === 'DRAFT' && (voucher.createdBy === user?.id || user?.role === 'admin') && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/${voucher.id}/edit`)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500"
                              onClick={() => setDeleteId(voucher.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <p className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredVouchers.length)} of {filteredVouchers.length} vouchers
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, arr) => (
                        <React.Fragment key={page}>
                          {index > 0 && arr[index - 1] !== page - 1 && (
                            <span className="px-2 text-muted-foreground">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-[#6a5da4] hover:bg-[#5a4d94]' : ''}`}
                            onClick={() => setCurrentPage(page)}
                            data-testid={`pagination-page-${page}`}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voucher</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this voucher? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VoucherListPage;
